import { Fragment, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Empty, Link, Rule, Select, ui } from '../ui';
import { CURRENCIES } from '@goodbuild/rules/ledger';
import { CURATION_DECISIONS } from '@goodbuild/rules/curation';
import { STORE_PLATFORMS, type StorePlatform } from '@goodbuild/rules/contracts';
import { storePlatformLabel } from '@goodbuild/rules/store-links';
import { formatBytes, formatDate, formatMoney } from '@goodbuild/rules/format';
import { ApiError, useApi, useApiMutation } from '../api';
import { Field, Loading, Screen, Table } from './shared';
import { CreemSync } from './creem-sync';
import { RELEASE_STATE_OPTIONS, type AdminGame } from './admin-games';

/**
 * One title, on a page of its own (#136).
 *
 * **Editing a game was a row that grew.** Pressing `Edit` on the games table
 * expanded a panel underneath it — rename, price, studio, then the eight art
 * slots — inside a `<td colSpan={8}>` on a table of the whole catalogue. It
 * worked, and everything wrong with it followed from where it was:
 *
 *   - **There was no address for a game.** Nothing to send somebody, nothing to
 *     bookmark, and no way to open two titles side by side. The only route into
 *     any title was `/admin/games` plus a press, and the press left no trace in
 *     the URL — so Back closed the panel instead of leaving the game, and a
 *     reload lost the place entirely.
 *   - **The width was a column of a table.** The art panel is eight rows of
 *     picture, dimensions and a file input, and it was being drawn inside a
 *     cell whose width is decided by the longest studio name in the catalogue.
 *   - **It could only show what the list happened to carry.** A row of `GET
 *     /games` has no gameplay link and no `updated_at`, because they are of no
 *     use in a table — so the two facts somebody opens a title to check were
 *     the two the panel could not print.
 *   - **And it was never a whole title.** Curation and release stayed in the
 *     row above, so *what state is this in* and *what can I change about it*
 *     were two different places, one of them scrolled off the top.
 *
 * So this is the whole title in one screen, addressed by id, reading
 * `GET /v1/admin/games/:productId`: what it is at the top, then every control
 * that changes it, in the order somebody actually works through them.
 *
 * ## What is not here
 *
 * **The address.** A slug is a permanent public URL — every link anybody has
 * shared to `goodbuild.cc/g/<slug>` and every mention of it anywhere — so
 * changing one silently breaks all of them. Renaming the *title* is the thing
 * that was asked for and the thing that is safe; moving a title's address is a
 * migration with redirects, not a text field on an admin page.
 *
 * **Deleting.** There is no endpoint and there should not be: a title with
 * orders against it is referenced by the ledger, and `delisted` is what taking
 * one down means here.
 *
 * ## The table keeps its controls
 *
 * Curation and release are still on `/admin/games` as well as here, which is
 * deliberate duplication rather than a leftover. The table is where somebody
 * triages — approve four titles, move one to `coming_soon` — and making that
 * a page load per title would be worse at the job the table is for. This page
 * is where somebody works on *one* game, and a page about one game that could
 * not tell you its curation state would be missing the first thing anybody
 * wants from it. Both post to the same two endpoints.
 */
export function AdminGameRoute(): React.ReactElement {
  const { productId } = useParams({ strict: false }) as { productId?: string };
  const id = productId ?? '';

  const key = ['admin', 'game', id] as const;
  const query = useApi<{ game: AdminGame }>(key, `/admin/games/${id}`, { enabled: id !== '' });

  /*
    The studios query was here and went with the studios (#144). It filled the
    picker that moved a title from one organisation to another; the byline is a
    string on the title itself now and the `Studio` section below edits it in
    place.
  */

  const call = useApiMutation();
  const cache = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Returns whether it worked, so a form keeps what was typed when it did not.
   *
   * The shape `admin-studio.tsx` uses, and for the same reason it is written
   * out there rather than imported from `useAdminAction`: that hook is built
   * around a table where one row at a time is busy, and this screen has one
   * subject — there is no id to be busy, and threading one through would be a
   * parameter every call site passed the same value to.
   *
   * Three caches are dropped rather than one. This title's own, obviously; the
   * games list, because every control here changes a cell on it; and the
   * overview, whose counts move when a title is approved or released.
   */
  async function act(path: string, init: RequestInit): Promise<boolean> {
    setBusy(true);
    setMessage(null);
    try {
      const result = await call<{ ok: string }>(path, init);
      setMessage(result.ok);
      await cache.invalidateQueries({ queryKey: key });
      await cache.invalidateQueries({ queryKey: ['admin', 'games'] });
      await cache.invalidateQueries({ queryKey: ['admin', 'overview'] });
      return true;
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'That did not work. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const game = query.data?.game;

  return (
    <Screen
      title={game?.name ?? 'Title'}
      aside={game ? `goodbuild.cc/g/${game.slug}` : undefined}
      query={query}
    >
      {message ? (
        <p className="gb-body" role="status">
          {message}
        </p>
      ) : null}

      {game ? (
        <div className={ui.stack ?? ''}>
          <p className={ui.actions ?? ''}>
            <Link href="/admin/games" className={ui.buttonSecondary ?? ''}>
              All titles
            </Link>
            <Link href={`/g/${game.slug}`} className={ui.buttonSecondary ?? ''}>
              Open the store page
            </Link>
          </p>

          <Overview game={game} />
          <Rule />
          <Curation game={game} busy={busy} onAct={act} />
          <Rule />
          <Release game={game} busy={busy} onAct={act} />
          <Rule />
          <Naming game={game} busy={busy} onAct={act} />
          <Rule />
          <Gameplay game={game} busy={busy} onAct={act} />
          <Rule />
          <StoreLinks game={game} busy={busy} onAct={act} />
          <Rule />
          <Description game={game} busy={busy} onAct={act} />
          <Rule />
          <Prices game={game} busy={busy} onAct={act} />
          <Rule />
          <CreemSync
            path={`/admin/games/${game.id}/creem-sync`}
            hint="Creem takes the money, so it needs its own copy of this title. It gets one when the title is approved, released and priced in euros or dollars — and this sends it now rather than waiting for the quarter-hourly sweep."
          />
          <Rule />
          <Studio game={game} busy={busy} onAct={act} />
          <Rule />
          <ArtPanel
            productId={game.id}
            name={game.name}
            steamLink={game.storeLinks.find((entry) => entry.platform === 'steam')?.url ?? ''}
          />
          <Rule />
          <MediaPanel
            productId={game.id}
            name={game.name}
            steamLink={game.storeLinks.find((entry) => entry.platform === 'steam')?.url ?? ''}
          />
        </div>
      ) : null}
    </Screen>
  );
}

/**
 * Where else the game is sold.
 *
 * **Three fields, and the Steam one has a second job.** All three become the
 * *Also available on* block on the store page; the Steam address is also what
 * the description and art importers use, so filling it in here is what stops
 * either of them asking for it again. That is the point of storing it rather
 * than typing it each time.
 *
 * Each row saves on its own. A single Save for all three would mean one bad
 * address refusing the two good ones beside it, and the refusals here are
 * per-link by nature — a host that is not the platform's own is a fact about one
 * field.
 *
 * The panel does not validate. `normaliseStoreLink` decides what counts as a
 * Steam address and it decides once, on the server, for the reason that file
 * gives — a second opinion in a form is how the two come to disagree about the
 * same string. What comes back is a sentence, and it is shown.
 */
function StoreLinks({ game, busy, onAct }: SectionProps): React.ReactElement {
  const stored = new Map(game.storeLinks.map((link) => [link.platform, link.url]));

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Store links</h2>

      <p className={ui.statLabel ?? ''}>
        Other shops selling this game. They show on the store page under &ldquo;Also available
        on&rdquo;. The Steam address is remembered and reused by the description and art imports
        below, so it only needs pasting once.
      </p>

      {STORE_PLATFORMS.map((platform) => (
        <StoreLinkField
          key={platform}
          platform={platform}
          gameId={game.id}
          saved={stored.get(platform) ?? ''}
          busy={busy}
          onAct={onAct}
        />
      ))}
    </section>
  );
}

const LINK_PLACEHOLDERS: Readonly<Record<StorePlatform, string>> = {
  steam: 'https://store.steampowered.com/app/730/',
  epic: 'https://store.epicgames.com/p/some-game',
  itch: 'https://somebody.itch.io/some-game',
};

function StoreLinkField({
  platform,
  gameId,
  saved,
  busy,
  onAct,
}: {
  platform: StorePlatform;
  gameId: string;
  saved: string;
  busy: boolean;
  onAct: (path: string, init: RequestInit) => Promise<boolean>;
}): React.ReactElement {
  const [url, setUrl] = useState(saved);

  /*
   * The saved value wins whenever it changes underneath.
   *
   * The Steam field is written by a *different* control — pasting a link into
   * either importer saves it here — so without this the field would still be
   * showing whatever was in it before that happened. Derived during render
   * rather than in an effect, which is what actually decides it.
   */
  const [seen, setSeen] = useState(saved);
  if (seen !== saved) {
    setSeen(saved);
    setUrl(saved);
  }

  const trimmed = url.trim();
  const changed = trimmed !== saved;
  const path = `/admin/games/${gameId}/links/${platform}`;

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!changed || trimmed === '') return;
        void onAct(path, { method: 'PUT', body: JSON.stringify({ url: trimmed }) });
      }}
    >
      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        <input
          className={ui.input ?? ''}
          style={{ flex: '1 1 20rem' }}
          placeholder={LINK_PLACEHOLDERS[platform]}
          aria-label={`${storePlatformLabel(platform)} link`}
          value={url}
          onChange={(event) => setUrl(event.target.value)}
        />
        <button
          type="submit"
          className={ui.button ?? ''}
          disabled={busy || !changed || trimmed === ''}
        >
          Save {storePlatformLabel(platform)}
        </button>
        {saved !== '' ? (
          <button
            type="button"
            className={ui.buttonSecondary ?? ''}
            disabled={busy}
            onClick={() => {
              void onAct(path, { method: 'DELETE' });
            }}
          >
            Remove
          </button>
        ) : null}
      </div>
    </form>
  );
}

/**
 * The words on the store page.
 *
 * **The store page used to write this itself**, out of the reviews players left,
 * under a label saying nobody here had written it. It does not any more, so this
 * is where the text comes from — typed, or taken off the game's own Steam page.
 *
 * Two controls and one field between them, which is the shape that matters: the
 * import writes straight into the description and then the textarea holds what
 * it wrote, so the ordinary flow is *fetch, read it, fix it, save*. An import
 * nobody has edited stays marked as Steam's; the moment somebody saves, it is
 * ours and it has their name on it. That is the whole of the provenance model
 * and it is a database constraint, not a convention — see
 * `db/schema/descriptions.ts`.
 */
function Description({ game, busy, onAct }: SectionProps): React.ReactElement {
  const stored = game.description?.body ?? '';
  const steamLink = game.storeLinks.find((entry) => entry.platform === 'steam')?.url ?? '';
  const [body, setBody] = useState(stored);
  const [link, setLink] = useState('');

  /*
   * The stored text wins whenever it changes underneath.
   *
   * A Steam import is a write from a *different* control on this same section,
   * so without this the textarea would still be showing whatever was in it
   * before the fetch — and somebody would press Save and put the old text back
   * over the one they had just imported. Keyed on the stored value rather than
   * an effect, because that is what actually decides it.
   */
  const [seen, setSeen] = useState(stored);
  if (seen !== stored) {
    setSeen(stored);
    setBody(stored);
  }

  const trimmed = body.trim();
  const changed = trimmed !== stored.trim();

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Store description</h2>

      <p className={ui.statLabel ?? ''}>
        {game.description === null
          ? 'Nothing on the page yet. The store page says so rather than filling the space.'
          : game.description.source === 'steam'
            ? `Taken off Steam app ${game.description.steamAppId}. Nobody here has signed it, and the store page says where it came from.`
            : 'Written here. It has an author and a date.'}
      </p>

      {/*
        The import, which is the fast path: most titles here already have a
        Steam page and the studio has already written the words on it. It
        overwrites whatever is stored, which is what somebody wants after a
        studio rewrites its copy.

        The field is empty by default and that is fine: with nothing in it the
        import uses the Steam link saved above, and with something in it that
        address is saved and then used. Either way it is remembered.
      */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (link.trim() === '' && steamLink === '') return;
          void onAct(`/admin/games/${game.id}/description/steam`, {
            method: 'POST',
            body: JSON.stringify({ link: link.trim() }),
          });
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
          <input
            className={ui.input ?? ''}
            style={{ flex: '1 1 20rem' }}
            placeholder={
              steamLink === '' ? 'https://store.steampowered.com/app/730/' : `Using ${steamLink}`
            }
            aria-label={`Steam link for ${game.name}`}
            value={link}
            onChange={(event) => setLink(event.target.value)}
          />
          <button
            type="submit"
            className={ui.buttonSecondary ?? ''}
            disabled={busy || (link.trim() === '' && steamLink === '')}
          >
            Fetch from Steam
          </button>
        </div>
      </form>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!changed || trimmed === '') return;
          void onAct(`/admin/games/${game.id}/description`, {
            method: 'PUT',
            body: JSON.stringify({ body: trimmed }),
          });
        }}
      >
        <div className={ui.stack ?? ''}>
          <Field label="Description">
            <textarea
              className={ui.input ?? ''}
              rows={12}
              value={body}
              placeholder="What is this game?"
              onChange={(event) => setBody(event.target.value)}
            />
          </Field>

          <p className={ui.statLabel ?? ''}>
            Type prose and a blank line starts a new paragraph. Paste markup and it is kept &mdash;
            headings, lists, bold, and images hosted by Steam. Everything saved here is sanitised
            first, so anything outside that set is dropped rather than rendered. An imported
            description arrives as the markup Steam had, which is why this box shows tags.
          </p>

          <div className={ui.actions ?? ''}>
            <button type="submit" className={ui.button ?? ''} disabled={busy || !changed}>
              Save the description
            </button>
            {game.description ? (
              <button
                type="button"
                className={ui.buttonSecondary ?? ''}
                disabled={busy}
                onClick={() => {
                  void onAct(`/admin/games/${game.id}/description`, { method: 'DELETE' });
                }}
              >
                Remove it
              </button>
            ) : null}
          </div>
        </div>
      </form>
    </section>
  );
}

/** What one control on this page does. Every section below takes this shape. */
interface SectionProps {
  game: AdminGame;
  busy: boolean;
  onAct: (path: string, init: RequestInit) => Promise<boolean>;
}

/**
 * A label and a fact, down the page.
 *
 * A definition list rather than a table, because that is what it is: none of
 * these is a row of anything and there is nothing to sort or scan across. The
 * grid is inline for the reason `column` in `shared.tsx` gives — these screens
 * cannot declare CSS beside their markup, and one more name in the stylesheet
 * for a two-column list used once is worse than the four properties.
 */
function Facts({ rows }: { rows: readonly { label: string; value: ReactNode }[] }) {
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(7rem, max-content) 1fr',
        gap: 'var(--gb-space-2) var(--gb-space-6)',
        margin: 0,
      }}
    >
      {rows.map((row) => (
        <Fragment key={row.label}>
          <dt className={ui.statLabel ?? ''}>{row.label}</dt>
          <dd className="gb-body" style={{ margin: 0 }}>
            {row.value}
          </dd>
        </Fragment>
      ))}
    </dl>
  );
}

/**
 * Everything about the title that is a fact rather than a control.
 *
 * At the top, because the first question anybody arriving here has is *what am
 * I looking at* — and because half of these were unreadable anywhere in the
 * admin panel before this page existed.
 *
 * **The prices are here as well as in the form below**, and that is not a
 * duplicate: this is the one-line answer (what does it cost), the section below
 * is the whole table with a region column. Somebody checking a price should not
 * have to scroll past four other controls to see one.
 *
 * Every gap is a sentence rather than a dash. *Not priced. Nothing can be
 * bought until it is.* says what the empty state means; `—` says only that the
 * author had nothing to put there, which is the failure section 8 is about.
 */
function Overview({ game }: { game: AdminGame }): React.ReactElement {
  const euro = game.prices.find((entry) => entry.currency === 'EUR') ?? game.prices[0];

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>What it is</h2>

      <Facts
        rows={[
          { label: 'Studio', value: game.developerName },
          { label: 'Type', value: game.type.replace(/_/g, ' ') },
          { label: 'Curation', value: game.curationState.replace(/_/g, ' ') },
          { label: 'Release', value: game.releaseState.replace(/_/g, ' ') },
          {
            label: 'Price',
            value: euro
              ? formatMoney(euro.currency as never, euro.amountMinor)
              : 'Not priced. Nothing can be bought until it is.',
          },
          {
            label: 'Build',
            value: game.currentBuild
              ? `${game.currentBuild.version} — ${formatBytes(game.currentBuild.totalBytes)}`
              : 'Nothing pushed yet, so there is nothing to install.',
          },
          {
            label: 'Gameplay',
            value: game.gameplayUrl ? (
              <a href={game.gameplayUrl} target="_blank" rel="noopener noreferrer">
                {game.gameplayUrl}
              </a>
            ) : (
              'No link. Curation has nothing to watch.'
            ),
          },
          {
            label: 'Released',
            value: game.releasedAt
              ? formatDate(new Date(game.releasedAt))
              : 'Not yet. The date is stamped the first time it goes on the store.',
          },
          { label: 'Added', value: formatDate(new Date(game.createdAt)) },
          { label: 'Last change', value: formatDate(new Date(game.updatedAt)) },
        ]}
      />
    </section>
  );
}

/**
 * What each of the five states means, in a sentence.
 *
 * The state name on its own is a word from an enum, and two of these words are
 * ones somebody could reasonably misread: `draft` sounds like *we have not
 * finished with it* when it means *nobody has asked us to look*, and `approved`
 * sounds like the last step when there is still a contract to sign before the
 * title can go on the store. Both of those are the sort of thing that is
 * obvious to whoever built the queue and to nobody else.
 *
 * Keyed on the column's values with a fallback, so a state added later reads as
 * itself rather than as a blank paragraph.
 */
const CURATION_NOTE: Readonly<Record<string, string>> = {
  draft: 'A draft. Nobody has asked us to look at it yet.',
  submitted: 'Submitted, and waiting for somebody here to read it.',
  in_review: 'Being read. Nothing goes on the store until it is approved.',
  approved:
    'Approved — and the publishing agreement went out on that approval. It has to be signed before this can go on the store.',
  rejected:
    'Rejected. It can be sent back into review if that was wrong, or if the studio has changed something.',
};

/**
 * Where the title is in review, and the three things that can be done about it.
 *
 * A decision rather than a state picker, which is the same rule the games table
 * follows and the reason `CURATION_DECISIONS` has three entries where the
 * column has five. `draft` and `submitted` are the studio's: an administrator
 * writing `submitted` is submitting on the studio's behalf, and one writing
 * `draft` is quietly pulling work out of a queue somebody is waiting on.
 *
 * The button for the state it is already in is disabled rather than hidden, so
 * the row of buttons does not reflow every time a decision lands.
 */
function Curation({ game, busy, onAct }: SectionProps): React.ReactElement {
  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Curation</h2>

      <p className="gb-body">
        {CURATION_NOTE[game.curationState] ?? game.curationState.replace(/_/g, ' ')}
      </p>

      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        {CURATION_DECISIONS.map((decision) => (
          <button
            key={decision}
            type="button"
            className={ui.buttonSecondary ?? ''}
            disabled={busy || game.curationState === decision}
            onClick={() => {
              void onAct(`/admin/games/${game.id}/curation`, {
                method: 'POST',
                body: JSON.stringify({ decision }),
              });
            }}
          >
            {decision.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Whether it is on the store, which is a state and genuinely a picker.
 *
 * All four states are ours to set and `coming_soon` after `released` is a real
 * thing somebody does, so unlike curation this is a list to choose from rather
 * than a set of decisions. The server refuses `released` for a title that is
 * not approved or has no signed agreement, and says which — the refusal is not
 * restated here, because two copies of that rule is how a page comes to promise
 * something the endpoint will not do.
 */
function Release({ game, busy, onAct }: SectionProps): React.ReactElement {
  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Release</h2>

      <Select
        options={RELEASE_STATE_OPTIONS}
        value={game.releaseState}
        disabled={busy}
        onChange={(state) => {
          void onAct(`/admin/games/${game.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ releaseState: state }),
          });
        }}
        aria-label={`Release state for ${game.name}`}
      />

      <p className={ui.statLabel ?? ''}>
        Going to <em>released</em> needs an approved title and a signed publishing agreement, and
        stamps the release date the first time only — a title that is delisted and put back keeps
        the date it first went out.
      </p>
    </section>
  );
}

/** Renaming, and the address that does not move with it. */
function Naming({ game, busy, onAct }: SectionProps): React.ReactElement {
  const [name, setName] = useState(game.name);
  const trimmed = name.trim();

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Name</h2>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed && trimmed !== game.name)
            void onAct(`/admin/games/${game.id}`, {
              method: 'PATCH',
              body: JSON.stringify({ name: trimmed }),
            });
        }}
      >
        <div className={ui.stack ?? ''}>
          <Field label="Name">
            <input
              className={ui.input ?? ''}
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={120}
            />
          </Field>

          <p className={ui.statLabel ?? ''}>
            The address stays goodbuild.cc/g/{game.slug}. Renaming does not move a title, because
            every link anybody has shared to it points at that address.
          </p>

          <div className={ui.actions ?? ''}>
            <button
              type="submit"
              className={ui.button ?? ''}
              disabled={busy || !trimmed || trimmed === game.name}
            >
              Rename
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

/**
 * The video curation watches (#136, #137).
 *
 * **This is the control the whole page was missing.** The link was write-once
 * at submission and readable only in the curation queue, which was survivable
 * while both doors asked for it. #137 stopped the admin door asking — staff
 * adding a title for a studio that has signed already know what the gameplay is
 * — so a title typed in here has none by default, and without somewhere to set
 * one the queue would say *no gameplay link was sent* for ever.
 *
 * **Clearing it is a real action rather than an accident.** Links rot: an
 * unlisted video is taken down, a Drive share is revoked, a channel is
 * reorganised. A dead link in the queue is worse than no link, because it reads
 * as evidence that exists right up until somebody clicks it. So the empty field
 * saves as null and the button says so.
 */
function Gameplay({ game, busy, onAct }: SectionProps): React.ReactElement {
  const [link, setLink] = useState(game.gameplayUrl ?? '');
  const trimmed = link.trim();
  const changed = trimmed !== (game.gameplayUrl ?? '');

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Gameplay</h2>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!changed) return;
          void onAct(`/admin/games/${game.id}`, {
            method: 'PATCH',
            // Null clears it. An empty string would be parsed as a URL and
            // refused, which is the wrong answer to "there is no video".
            body: JSON.stringify({ gameplayUrl: trimmed === '' ? null : trimmed }),
          });
        }}
      >
        <div className={ui.stack ?? ''}>
          <Field label="Link">
            <input
              className={ui.input ?? ''}
              value={link}
              placeholder="https://youtu.be/…"
              onChange={(event) => setLink(event.target.value)}
            />
          </Field>

          <p className={ui.statLabel ?? ''}>
            What the curation queue puts in front of the person deciding. Any host — a raw capture
            is fine and an unlisted video is fine. Emptying the field removes it, which is the right
            thing to do with a link that has stopped working.
          </p>

          <div className={ui.actions ?? ''}>
            <button type="submit" className={ui.button ?? ''} disabled={busy || !changed}>
              {trimmed === '' ? 'Remove the link' : 'Save the link'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

/**
 * The currencies, on the ledger's own tuple.
 *
 * The codes are already the labels — `EUR` is not improved by being spelled
 * out, and the figures beside it are the same three letters. Module scope
 * because the form below re-renders on every keystroke of the amount field.
 */
const CURRENCY_OPTIONS = CURRENCIES.map((code) => ({ value: code, label: code }));

/**
 * What it costs, everywhere it costs something.
 *
 * Section 18: pricing is per-region tables and never an FX conversion, so a
 * blank country sets the fallback for a currency and a country sets the price
 * in that country alone. Both are live rows; the ones they replace are closed
 * rather than overwritten, because an order that cleared last week cleared at
 * last week's price and a table that forgot that makes every historical order
 * unexplainable.
 */
function Prices({ game, busy, onAct }: SectionProps): React.ReactElement {
  const [currency, setCurrency] = useState('EUR');
  const [amount, setAmount] = useState('');
  const [country, setCountry] = useState('');

  const major = Number(amount);
  const valid = amount.trim() !== '' && Number.isFinite(major) && major >= 0;

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Price</h2>

      {game.prices.length === 0 ? (
        <Empty>Not priced. Nothing can be bought until it is.</Empty>
      ) : (
        <Table head={['Currency', 'Region', 'Amount']}>
          {game.prices.map((entry) => (
            <tr key={`${entry.currency}-${entry.countryCode ?? 'default'}`}>
              <td>{entry.currency}</td>
              <td>{entry.countryCode ?? 'Everywhere else'}</td>
              <td className={ui.numeric ?? ''}>
                {formatMoney(entry.currency as never, entry.amountMinor)}
              </td>
            </tr>
          ))}
        </Table>
      )}

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid) return;
          void onAct(`/admin/games/${game.id}/price`, {
            method: 'PUT',
            body: JSON.stringify({
              currency,
              // Minor units are what the column stores; a person types 24.99.
              amountMinor: Math.round(major * 100),
              countryCode: country.trim() === '' ? null : country.trim().toUpperCase(),
            }),
          }).then((set) => {
            if (set) setAmount('');
          });
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
          <Select
            options={CURRENCY_OPTIONS}
            value={currency}
            onChange={setCurrency}
            aria-label="Currency"
            style={{ minWidth: '8rem' }}
          />
          <input
            className={ui.input ?? ''}
            style={{ width: '8rem' }}
            inputMode="decimal"
            placeholder="24.99"
            aria-label="Amount"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <input
            className={ui.input ?? ''}
            style={{ width: '8rem' }}
            placeholder="Country (optional)"
            aria-label="Country code"
            maxLength={2}
            value={country}
            onChange={(event) => setCountry(event.target.value)}
          />
          <button type="submit" className={ui.button ?? ''} disabled={busy || !valid}>
            Set price
          </button>
        </div>
      </form>

      <p className={ui.statLabel ?? ''}>
        Blank country sets the fallback for that currency. Prices are historised, not overwritten —
        an order that cleared last week cleared at last week&rsquo;s price.
      </p>
    </section>
  );
}

/**
 * The byline, which was a studio picker (#126, #144).

 * It moved a title from one organisation to another, and it was the one control
 * on this page that changed who got paid: `product.developer_id` was what the
 * royalty split was written against. There is no split and no organisation -
 * this is the string printed under the title on its store page, and getting it
 * wrong is a typo rather than a misdirected payment.
 *
 * Kept under the price rather than folded into `Naming` above, which renames
 * the title and its address. Those two are what a title *is called*; this is
 * who made it, and they are edited for different reasons on different days.
 */
function Studio({ game, busy, onAct }: SectionProps): React.ReactElement {
  const [name, setName] = useState(game.developerName);
  const trimmed = name.trim();

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Studio</h2>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed === '' || trimmed === game.developerName) return;
          void onAct(`/admin/games/${game.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ developerName: trimmed }),
          });
        }}
      >
        <div className={ui.stack ?? ''}>
          <Field label="Who made it">
            <input
              className={ui.input ?? ''}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="GOODBUILD"
              maxLength={120}
            />
          </Field>

          <p className={ui.statLabel ?? ''}>
            Printed under the title on its store page. It is not a link and there is no page behind
            it.
          </p>

          <div className={ui.actions ?? ''}>
            <button
              type="submit"
              className={ui.button ?? ''}
              disabled={busy || trimmed === '' || trimmed === game.developerName}
            >
              Save
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}

/**
 * Where the art thumbnails are fetched from.
 *
 * The API's own origin, because that is where the public content route lives —
 * this app is on `app.goodbuild.cc` and the images are on `api.goodbuild.cc`.
 * Read the same way `src/api.ts` reads it, from the one variable Vite inlines,
 * rather than through that module: `request` there adds a bearer token and
 * parses JSON, and neither is right for the `src` of an `<img>`.
 */
const API_ORIGIN =
  (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'https://api.goodbuild.cc';

/** One slot, as `GET /v1/admin/games/:id/art` reports it. */
interface ArtSlot {
  kind: string;
  steamName: string;
  surface: string;
  expected: string;
  orientation: 'landscape' | 'portrait';
  storeCritical: boolean;
  verifiedOn: string;
  art: {
    width: number;
    height: number;
    bytes: number;
    source: 'steam' | 'upload';
    sourceUrl: string | null;
    updatedAt: string;
  } | null;
}

/**
 * The eight slots a store page is drawn from, and how to fill one (#131-#134).
 *
 * **Every slot is listed, filled or not**, because the question somebody opens
 * this to answer is *what is missing*. A list of what exists cannot answer it,
 * and a store page with no capsule does not say so — it shows the art-pending
 * plate, which reads as a title nobody has got to yet rather than as a file
 * nobody uploaded.
 *
 * **The size is printed beside every slot, and it is Steam's** (#134). That is
 * the whole reason this is usable: a studio moving from Steam already has these
 * exact files, so the instruction is "the one you sent Valve" rather than a
 * crop somebody has to produce. The server refuses anything that is not the
 * exact size and names both numbers when it does — the check is not restated
 * here, because two copies of a dimension table is how a form comes to promise
 * something the endpoint refuses.
 *
 * **The picture is the proof.** A filled slot renders the actual object from
 * the public content route rather than a description of it: after an import,
 * what somebody needs to see is that the capsule is the right *game*, and a
 * header fetched for the wrong app id is a correct-looking row above an
 * obviously wrong picture.
 *
 * It moved here from the games table with the rest of the editor (#136), and it
 * is the part of that panel that most needed the room: eight rows of picture,
 * dimensions and a file input were being drawn inside a table cell.
 */
function ArtPanel({
  productId,
  name,
  steamLink,
}: {
  productId: string;
  name: string;
  /** The saved Steam address, which the import falls back to. */
  steamLink: string;
}): React.ReactElement {
  const key = ['admin', 'games', productId, 'art'] as const;
  const query = useApi<{ slots: ArtSlot[]; count: number }>(key, `/admin/games/${productId}/art`);
  const call = useApiMutation();
  const cache = useQueryClient();

  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [link, setLink] = useState('');

  const slots = query.data?.slots ?? [];

  async function act(slot: string, run: () => Promise<{ ok: string }>): Promise<void> {
    setBusy(slot);
    setMessage(null);
    try {
      setMessage((await run()).ok);
      await cache.invalidateQueries({ queryKey: key });
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Upload one file into one slot.
   *
   * The file itself as the body — there is exactly one part and the slot is
   * already in the path, so a multipart form would mean parsing a boundary to
   * recover the bytes the request already is. The content type is set
   * explicitly because `useApiMutation` sends `application/json` for any body,
   * which would be a lie about a PNG; the server does not read it either way,
   * because `probeImage` reads the bytes.
   */
  function upload(kind: string, file: File): void {
    void act(kind, () =>
      call<{ ok: string }>(`/admin/games/${productId}/art/${kind}`, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream' },
      }),
    );
  }

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Store art</h2>

      <p className={ui.statLabel ?? ''}>
        Eight slots, every one the size Steam asks for &mdash; so whatever the studio sent Valve is
        the file that fits here. Anything else is refused with both numbers in the message.
      </p>

      {/*
        Re-running the import, which is not only for a title that missed it.

        A studio redoing its capsules on Steam is the ordinary reason to come
        back here, and re-running overwrites each slot in place rather than
        adding to it. The field is empty by default: with nothing in it the
        import uses the Steam link saved under Store links, and with something in
        it that address is saved there and then used.
      */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (link.trim() === '' && steamLink === '') return;
          void act('steam', () =>
            call<{ ok: string }>(`/admin/games/${productId}/art/steam`, {
              method: 'POST',
              body: JSON.stringify({ link: link.trim() }),
            }),
          );
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
          <input
            className={ui.input ?? ''}
            style={{ flex: '1 1 20rem' }}
            placeholder={
              steamLink === '' ? 'https://store.steampowered.com/app/730/' : `Using ${steamLink}`
            }
            aria-label={`Steam link for ${name}`}
            value={link}
            onChange={(event) => setLink(event.target.value)}
          />
          <button
            type="submit"
            className={ui.buttonSecondary ?? ''}
            disabled={busy !== null || (link.trim() === '' && steamLink === '')}
          >
            {busy === 'steam' ? 'Reading Steam' : 'Fetch from Steam'}
          </button>
        </div>
      </form>

      {message ? (
        <p className="gb-body" role="status">
          {message}
        </p>
      ) : null}

      {query.isPending ? <Loading label="Reading the art" /> : null}

      <div className={ui.stack ?? ''}>
        {slots.map((slot) => (
          <div key={slot.kind} className={ui.point ?? ''}>
            <div
              style={{
                display: 'flex',
                gap: 'var(--gb-space-4)',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              {slot.art ? (
                /*
                  The object itself, from the public content route.

                  Capped rather than drawn at the slot's own size: a 3840-wide
                  library hero would otherwise be a screenful of panel. What
                  matters here is *which game is this*, and a thumbnail answers
                  it.
                */
                <img
                  src={`${API_ORIGIN}/v1/content/title-art/${productId}/${slot.kind}`}
                  alt=""
                  style={{
                    width: slot.orientation === 'portrait' ? '5rem' : '9rem',
                    height: 'auto',
                    border: 'var(--gb-rule-width) solid var(--gb-rule)',
                  }}
                />
              ) : null}

              <div className={ui.stack ?? ''} style={{ flex: '1 1 16rem' }}>
                <p className="gb-body" style={{ margin: 0 }}>
                  <strong>{slot.surface}</strong>
                  {slot.storeCritical ? ' — the store needs this one' : ''}
                </p>
                <p className={ui.statLabel ?? ''}>
                  {slot.expected} &middot; Steam&rsquo;s {slot.steamName} &middot; checked{' '}
                  {slot.verifiedOn}
                </p>
                <p className={ui.statLabel ?? ''}>
                  {slot.art
                    ? `${slot.art.width}×${slot.art.height}, ${Math.round(
                        slot.art.bytes / 1024,
                      )} kB, ${slot.art.source === 'steam' ? 'fetched from Steam' : 'uploaded'}`
                    : 'Empty.'}
                </p>

                <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    aria-label={`${slot.surface} for ${name}`}
                    disabled={busy !== null}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      // Cleared so choosing the same file twice — after a
                      // refusal that was fixed in an editor — fires again.
                      event.target.value = '';
                      if (file) upload(slot.kind, file);
                    }}
                  />
                  {slot.art ? (
                    <button
                      type="button"
                      className={ui.buttonSecondary ?? ''}
                      disabled={busy !== null}
                      onClick={() =>
                        void act(slot.kind, () =>
                          call<{ ok: string }>(`/admin/games/${productId}/art/${slot.kind}`, {
                            method: 'DELETE',
                          }),
                        )
                      }
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

interface AdminMediaItem {
  id: string;
  kind: 'trailer' | 'screenshot';
  positionInGallery: number;
  contentType: string;
  width: number | null;
  height: number | null;
  bytes: number;
  hasPoster: boolean;
  source: 'steam' | 'upload';
  sourceUrl: string | null;
  updatedAt: string;
}

function MediaPanel({
  productId,
  name,
  steamLink,
}: {
  productId: string;
  name: string;
  steamLink: string;
}): React.ReactElement {
  const key = ['admin', 'games', productId, 'media'] as const;
  const query = useApi<{ media: AdminMediaItem[]; count: number }>(
    key,
    `/admin/games/${productId}/media`,
  );
  const call = useApiMutation();
  const cache = useQueryClient();

  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [link, setLink] = useState('');

  const media = query.data?.media ?? [];

  async function act(subject: string, run: () => Promise<{ ok: string }>): Promise<void> {
    setBusy(subject);
    setMessage(null);
    try {
      setMessage((await run()).ok);
      await cache.invalidateQueries({ queryKey: key });
    } catch (error) {
      setMessage(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  }

  function upload(file: File): void {
    void act('add', () =>
      call<{ ok: string }>(`/admin/games/${productId}/media`, {
        method: 'POST',
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream' },
      }),
    );
  }

  function uploadPoster(mediaId: string, file: File): void {
    void act(mediaId, () =>
      call<{ ok: string }>(`/admin/games/${productId}/media/${mediaId}/poster`, {
        method: 'PUT',
        body: file,
        headers: { 'content-type': file.type || 'application/octet-stream' },
      }),
    );
  }

  function move(mediaId: string, step: -1 | 1): void {
    const from = media.findIndex((item) => item.id === mediaId);
    const to = from + step;
    if (from < 0 || to < 0 || to >= media.length) return;

    const order = media.map((item) => item.id);
    const moved = order[from];
    const displaced = order[to];
    if (moved === undefined || displaced === undefined) return;
    order[from] = displaced;
    order[to] = moved;

    void act(mediaId, () =>
      call<{ ok: string }>(`/admin/games/${productId}/media/order`, {
        method: 'PUT',
        body: JSON.stringify({ order }),
      }),
    );
  }

  return (
    <section className={ui.stack ?? ''}>
      <h2 className={ui.sectionTitle ?? ''}>Screenshots and trailers</h2>

      <p className={ui.statLabel ?? ''}>
        What the store page opens with, in this order. Trailers play in place, and the first thing
        on the list is the first thing somebody sees. PNG, JPEG and WebP for pictures, MP4 and WebM
        for video, up to 64&nbsp;MB each.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (link.trim() === '' && steamLink === '') return;
          void act('steam', () =>
            call<{ ok: string }>(`/admin/games/${productId}/media/steam`, {
              method: 'POST',
              body: JSON.stringify({ link: link.trim() }),
            }),
          );
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
          <input
            className={ui.input ?? ''}
            style={{ flex: '1 1 20rem' }}
            placeholder={
              steamLink === '' ? 'https://store.steampowered.com/app/730/' : `Using ${steamLink}`
            }
            aria-label={`Steam link for ${name}`}
            value={link}
            onChange={(event) => setLink(event.target.value)}
          />
          <button
            type="submit"
            className={ui.buttonSecondary ?? ''}
            disabled={busy !== null || (link.trim() === '' && steamLink === '')}
          >
            {busy === 'steam' ? 'Reading Steam' : 'Replace from Steam'}
          </button>
        </div>
      </form>

      <p className={ui.statLabel ?? ''}>
        Fetching from Steam clears what is here and puts Valve&rsquo;s own gallery in its place,
        trailers first. Running it again is safe.
      </p>

      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,video/mp4,video/webm"
          aria-label={`Add a screenshot or trailer for ${name}`}
          disabled={busy !== null}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) upload(file);
          }}
        />
      </div>

      {message ? (
        <p className="gb-body" role="status">
          {message}
        </p>
      ) : null}

      {query.isPending ? <Loading label="Reading the gallery" /> : null}

      {!query.isPending && media.length === 0 ? (
        <p className="gb-body">Nothing yet. The store page shows no gallery until there is one.</p>
      ) : null}

      <div className={ui.stack ?? ''}>
        {media.map((item, index) => (
          <div key={item.id} className={ui.point ?? ''}>
            <div
              style={{
                display: 'flex',
                gap: 'var(--gb-space-4)',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
              }}
            >
              {item.kind === 'screenshot' || item.hasPoster ? (
                <img
                  src={
                    item.kind === 'screenshot'
                      ? `${API_ORIGIN}/v1/content/title-media/${productId}/${item.id}`
                      : `${API_ORIGIN}/v1/content/title-media/${productId}/${item.id}/poster`
                  }
                  alt=""
                  style={{
                    width: '9rem',
                    height: 'auto',
                    border: 'var(--gb-rule-width) solid var(--gb-rule)',
                  }}
                />
              ) : null}

              <div className={ui.stack ?? ''} style={{ flex: '1 1 16rem' }}>
                <p className="gb-body" style={{ margin: 0 }}>
                  <strong>
                    {index + 1}. {item.kind === 'trailer' ? 'Trailer' : 'Screenshot'}
                  </strong>
                  {index === 0 ? ' — the one the page opens on' : ''}
                </p>
                <p className={ui.statLabel ?? ''}>
                  {item.width !== null && item.height !== null
                    ? `${item.width}×${item.height}, `
                    : ''}
                  {formatBytes(item.bytes)} &middot; {item.contentType} &middot;{' '}
                  {item.source === 'steam' ? 'fetched from Steam' : 'uploaded'}
                  {item.kind === 'trailer' && !item.hasPoster ? ' · no poster' : ''}
                </p>

                <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={ui.buttonSecondary ?? ''}
                    disabled={busy !== null || index === 0}
                    onClick={() => move(item.id, -1)}
                  >
                    Move up
                  </button>
                  <button
                    type="button"
                    className={ui.buttonSecondary ?? ''}
                    disabled={busy !== null || index === media.length - 1}
                    onClick={() => move(item.id, 1)}
                  >
                    Move down
                  </button>
                  <button
                    type="button"
                    className={ui.buttonSecondary ?? ''}
                    disabled={busy !== null}
                    onClick={() =>
                      void act(item.id, () =>
                        call<{ ok: string }>(`/admin/games/${productId}/media/${item.id}`, {
                          method: 'DELETE',
                        }),
                      )
                    }
                  >
                    Remove
                  </button>
                </div>

                {item.kind === 'trailer' ? (
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    aria-label={`Poster for trailer ${index + 1}`}
                    disabled={busy !== null}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      if (file) uploadPoster(item.id, file);
                    }}
                  />
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
